import React, { useEffect, useCallback, useState, useRef, useMemo } from 'react';
import { Outlet, Link, useNavigate, useParams } from '@tanstack/react-router';
import { useAuthStore } from '@/stores/auth.store';
import { useOrgStore } from '@/stores/org.store';
import { useChatStore } from '@/stores/chat.store';
import { usePresenceStore } from '@/stores/presence.store';
import { useUnreadStore } from '@/stores/unread.store';
import { useThreadStore } from '@/stores/thread.store';
import { useInboxStore } from '@/stores/inbox.store';
import { useTodosStore, useTodoBadgeTotal, applyTodosUpdateEvent } from '@/stores/todos.store';
import { useStatusStore } from '@/stores/status.store';
import { apiClient, fetchMe, fetchAreas, fetchDms, fetchOrgMembers, fetchUnreadCounts, sendMessage, fetchInbox, fetchMyStatus, fetchNotificationSettings, fetchMemberAvailability, presignProfilePicture, completeProfilePicture, removeProfilePicture, updateProfile, updateAreaSortOrder, updateOrgSortOrder, refreshTokenApi, fetchDraftCount, fetchDraftAreaIds, fetchTodos } from '@/lib/api';
import { useDraftStore } from '@/stores/draft.store';
import { wsClient } from '@/lib/ws';
import { useWebSocket, useSubscribeAreas } from '@/hooks/useWebSocket';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Dialog from '@radix-ui/react-dialog';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { SetStatusDialog } from '@/components/status/set-status-dialog';
import { DndSettings } from '@/components/status/dnd-settings';
import { UpgradeAccountDialog } from '@/components/upgrade-account-dialog';
import { EditProfileDialog } from '@/components/edit-profile-dialog';
import { AreaIcon } from '@/components/ui/icon-picker';
import { Tooltip } from '@/components/ui/tooltip';
import { CreateAreaDialog } from '@/components/chat/create-area-dialog';
import { CommandPalette } from '@/components/command-palette';
import { useThemeStore } from '@/stores/theme.store';
import { useOnboardingStore } from '@/stores/onboarding.store';
import { toast } from '@/stores/toast.store';
import { ToastContainer } from '@/components/ui/toast';
import { OnboardingTour } from '@/components/onboarding/onboarding-tour';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useIsOnline } from '@/components/chat/connection-status';
import type { WSServerEvent } from '@goodchat/shared/types';
import type { OrgArea, DmPreview, OrgMember } from '@/stores/org.store';

// Memoized content wrapper — prevents sidebar store re-renders from cascading into
// route components via <Outlet />. AppShell subscribes to 15+ store slices for badges/counts;
// without this, EVERY sidebar update re-renders the entire route tree.
const MemoizedContent = React.memo(function MemoizedContent({
  orgSwitching,
}: {
  orgSwitching: boolean;
}) {
  if (orgSwitching) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-500" />
      </div>
    );
  }
  return (
    <RouteErrorBoundary>
      <Outlet />
    </RouteErrorBoundary>
  );
});

// Error Boundary — catches rendering errors in child routes and shows the actual error message
// instead of a cryptic React error number in production
class RouteErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { error: Error | null; errorInfo: string }
> {
  state: { error: Error | null; errorInfo: string } = { error: null, errorInfo: '' };

  static getDerivedStateFromError(error: Error) {
    return { error, errorInfo: error.stack ?? '' };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('Route error:', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex flex-1 items-center justify-center p-8">
          <div className="max-w-lg text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
              <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.072 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-red-600 dark:text-red-400">Something went wrong</h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{this.state.error.message}</p>
            {this.state.errorInfo && (
              <pre className="mt-3 max-h-40 overflow-auto rounded-lg bg-gray-100 p-3 text-left text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                {this.state.errorInfo}
              </pre>
            )}
            <button
              onClick={() => {
                this.setState({ error: null, errorInfo: '' });
                window.location.reload();
              }}
              className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function SortableAreaItem({
  area,
  orgSlug,
  unread,
  hasDraft,
  isMuted,
}: {
  area: OrgArea;
  orgSlug: string;
  unread: number;
  hasDraft: boolean;
  isMuted: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: area.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="group relative flex items-center" {...attributes}>
      <span
        className="absolute left-0 flex h-6 w-4 flex-shrink-0 cursor-grab items-center justify-center opacity-0 transition-opacity group-hover:opacity-60"
        {...listeners}
        title="Drag to reorder"
      >
        <svg className="h-3 w-3 text-gray-400" viewBox="0 0 6 10" fill="currentColor">
          <circle cx="1" cy="1" r="1" />
          <circle cx="5" cy="1" r="1" />
          <circle cx="1" cy="5" r="1" />
          <circle cx="5" cy="5" r="1" />
          <circle cx="1" cy="9" r="1" />
          <circle cx="5" cy="9" r="1" />
        </svg>
      </span>
      <Link
        to="/org/$orgSlug/$areaSlug"
        params={{ orgSlug, areaSlug: area.slug }}
        className={`flex flex-1 items-center gap-2 rounded-lg px-3 py-1.5 text-sm transition-colors hover:bg-gray-200 dark:hover:bg-gray-800 [&.active]:bg-blue-50 [&.active]:font-medium [&.active]:text-blue-700 dark:[&.active]:bg-blue-900/40 dark:[&.active]:text-blue-300 ${isMuted ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}
      >
        <AreaIcon icon={area.icon} iconColor={area.iconColor} className={`h-4 w-4 ${isMuted ? 'opacity-50' : ''}`} />
        <span className={`flex-1 truncate ${unread > 0 && !isMuted ? 'font-semibold text-gray-900 dark:text-white' : ''}`}>{area.name}</span>
        {isMuted && (
          <svg className="h-3 w-3 flex-shrink-0 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.143 17.082a24.248 24.248 0 003.718.33M17.412 14.147A8.955 8.955 0 0018 9.75V9A6 6 0 006.53 6.53M6 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m3.714 0a3 3 0 11-5.714 0" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 3l18 18" />
          </svg>
        )}
        {hasDraft && !isMuted && (
          <svg className="h-3 w-3 flex-shrink-0 text-gray-400 dark:text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
          </svg>
        )}
        {unread > 0 && (
          <span className="min-w-[20px] rounded-full bg-blue-500 px-1.5 py-0.5 text-center text-[10px] font-bold text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </Link>
    </div>
  );
}

// Pure sort helper — outside component to avoid recreation
const sortAreas = (areaList: OrgArea[]) =>
  [...areaList].sort((a, b) => {
    const aOrder = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
    const bOrder = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return a.name.localeCompare(b.name);
  });

function navigateWithTransition(navigateFn: () => void) {
  if (typeof document !== 'undefined' && 'startViewTransition' in document) {
    (document as any).startViewTransition(() => navigateFn());
  } else {
    navigateFn();
  }
}

const LAST_ORG_KEY = 'goodchat_last_org';
const SIDEBAR_COLLAPSED_KEY = 'goodchat_sidebar_collapsed';
const GUEST_TARGET_KEY = 'goodchat_guest_target';

/** Play a subtle two-tone chime using the Web Audio API (no audio file needed). */
function playNotificationSound() {
  try {
    const ctx = new AudioContext();
    const gain = ctx.createGain();
    gain.gain.value = 0.12;
    gain.connect(ctx.destination);

    const now = ctx.currentTime;

    // First note: C5 (523 Hz) for 100ms
    const osc1 = ctx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.value = 523;
    osc1.connect(gain);
    osc1.start(now);
    osc1.stop(now + 0.1);

    // Second note: E5 (659 Hz) for 100ms
    const osc2 = ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.value = 659;
    osc2.connect(gain);
    osc2.start(now + 0.1);
    osc2.stop(now + 0.2);

    // Clean up the AudioContext after playback
    osc2.onended = () => ctx.close();
  } catch {
    // Silently ignore — browser may block AudioContext before user interaction
  }
}

export function AppShell() {
  const navigate = useNavigate();
  // Individual selectors — avoid subscribing to the ENTIRE store (which re-renders
  // AppShell + all children on ANY auth/org change, amplifying render cascades).
  const user = useAuthStore((s) => s.user);
  const orgs = useAuthStore((s) => s.orgs);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const accessToken = useAuthStore((s) => s.accessToken);
  const selectedOrgId = useOrgStore((s) => s.selectedOrgId);
  const areas = useOrgStore((s) => s.areas);
  const dms = useOrgStore((s) => s.dms);
  // Actions are stable refs — use getState() so we don't subscribe to store changes just for actions
  const { setOrgs, logout } = useAuthStore.getState();
  const { setSelectedOrg, setAreas, setMembers, setDms } = useOrgStore.getState();
  const orgIdBySlug = useOrgStore((s) => s.orgIdBySlug);
  const areaIdBySlug = useOrgStore((s) => s.areaIdBySlug);
  const areaSlugById = useOrgStore((s) => s.areaSlugById);
  const orgSlugById = useOrgStore((s) => s.orgSlugById);
  const setConnected = useChatStore((s) => s.setConnected);
  const typingByArea = useChatStore((s) => s.typingByArea);
  const countsByArea = useUnreadStore((s) => s.countsByArea);
  const setCounts = useUnreadStore((s) => s.setCounts);
  const inboxCounts = useInboxStore((s) => s.counts);
  const todoCount = useTodoBadgeTotal();
  const draftCount = useDraftStore((s) => s.count);
  const draftAreaIds = useDraftStore((s) => s.draftAreaIds);
  const areaNotificationLevels = useOrgStore((s) => s.areaNotificationLevels);
  const params = useParams({ strict: false }) as { orgSlug?: string; areaSlug?: string };

  // Resolve orgId and areaId from URL slugs (never fall back to raw slug strings)
  const orgId = params.orgSlug ? orgIdBySlug[params.orgSlug] ?? undefined : undefined;
  const areaId = params.areaSlug ? areaIdBySlug[params.areaSlug] ?? undefined : undefined;

  const [initialLoading, setInitialLoading] = useState(true);
  const [orgSwitching, setOrgSwitching] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'; } catch { return false; }
  });
  const sidebarFocusTrapRef = useFocusTrap<HTMLElement>(sidebarOpen);
  const [profileIncomplete, setProfileIncomplete] = useState(false);
  const [profileDisplayName, setProfileDisplayName] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(null);
  const [profilePictureFile, setProfilePictureFile] = useState<File | null>(null);
  const [profilePictureUploading, setProfilePictureUploading] = useState(false);
  const [profileMissingName, setProfileMissingName] = useState(false);
  const [profileMissingPicture, setProfileMissingPicture] = useState(false);
  const profilePicInputRef = useRef<HTMLInputElement>(null);

  // Update browser tab title with total unread count
  useEffect(() => {
    const total = Object.values(countsByArea).reduce((sum, n) => sum + n, 0);
    document.title = total > 0 ? `(${total}) GoodChat` : 'GoodChat';
  }, [countsByArea]);

  // Load user data + org data on mount (must complete before rendering child routes)
  useEffect(() => {
    if (!isAuthenticated) {
      setInitialLoading(false);
      return;
    }
    fetchMe()
      .then(async (me) => {
        setOrgs(me.orgs);
        useOrgStore.getState().setOrgSlugs(me.orgs);

        // Update user in auth store with fresh data (including presigned profilePictureUrl)
        const currentAuth = useAuthStore.getState();
        if (currentAuth.accessToken && currentAuth.refreshToken) {
          useAuthStore.getState().setAuth(
            {
              id: me.user.id,
              fullName: me.user.fullName,
              displayName: me.user.displayName,
              profilePictureUrl: me.user.profilePictureUrl,
              homeCity: me.user.homeCity,
              isGuest: me.user.isGuest,
              messageViewStyle: me.user.messageViewStyle,
            },
            currentAuth.accessToken,
            currentAuth.refreshToken,
          );
        }

        if (!me.profileComplete) {
          setProfileIncomplete(true);
          // Pre-fill display name if it exists
          if (me.user.displayName) {
            setProfileDisplayName(me.user.displayName);
            setProfileMissingName(false);
          } else {
            setProfileMissingName(true);
          }
          // Check if profile picture is missing (guests are exempt)
          if (!me.user.isGuest && !me.user.profilePictureUrl) {
            setProfileMissingPicture(true);
          } else {
            setProfileMissingPicture(false);
            if (me.user.profilePictureUrl) {
              setProfilePicturePreview(me.user.profilePictureUrl);
            }
          }
        }

        // Check for guest login target (set by guest-login page for direct area navigation)
        let guestTarget: { orgId: string; areaId: string } | null = null;
        try {
          const raw = sessionStorage.getItem(GUEST_TARGET_KEY);
          if (raw) {
            guestTarget = JSON.parse(raw);
            sessionStorage.removeItem(GUEST_TARGET_KEY);
          }
        } catch {}

        // Determine which org to load — restore last used org from localStorage
        let targetOrgSlug = params.orgSlug;
        if (!targetOrgSlug && me.orgs.length > 0) {
          // If guest target exists, prefer its org
          if (guestTarget) {
            const guestOrg = me.orgs.find((o) => o.id === guestTarget!.orgId);
            if (guestOrg) {
              targetOrgSlug = guestOrg.slug;
            }
          }
          if (!targetOrgSlug) {
            const savedSlug = localStorage.getItem(LAST_ORG_KEY);
            const savedOrgExists = savedSlug && me.orgs.some((o) => o.slug === savedSlug);
            targetOrgSlug = savedOrgExists ? savedSlug : me.orgs[0].slug;
          }
          navigate({ to: '/org/$orgSlug', params: { orgSlug: targetOrgSlug } });
        }

        // Resolve org ID and pre-load areas so areaIdBySlug is populated before child routes render
        const resolvedOrgId = targetOrgSlug
          ? me.orgs.find((o) => o.slug === targetOrgSlug)?.id
          : undefined;

        if (resolvedOrgId) {
          setSelectedOrg(resolvedOrgId);
          if (targetOrgSlug) localStorage.setItem(LAST_ORG_KEY, targetOrgSlug);
          const [areasRes, dmsRes, membersRes, unreadRes] = await Promise.all([
            fetchAreas(resolvedOrgId).catch((e) => { console.error('Failed to load areas:', e); return null; }),
            fetchDms(resolvedOrgId).catch((e) => { console.error('Failed to load DMs:', e); return null; }),
            fetchOrgMembers(resolvedOrgId).catch((e) => { console.error('Failed to load members:', e); return null; }),
            fetchUnreadCounts(resolvedOrgId).catch((e) => { console.error('Failed to load unread counts:', e); return null; }),
          ]);
          if (areasRes) setAreas(areasRes.areas);
          if (dmsRes) setDms(dmsRes.dms);
          if (membersRes) setMembers(membersRes.members);
          if (unreadRes) setCounts(unreadRes.counts);

          // If guest login target has a specific area, navigate directly to it
          if (guestTarget && areasRes && targetOrgSlug) {
            const targetArea = areasRes.areas.find((a: { id: string; slug?: string }) => a.id === guestTarget!.areaId);
            if (targetArea?.slug) {
              navigate({ to: '/org/$orgSlug/$areaSlug', params: { orgSlug: targetOrgSlug, areaSlug: targetArea.slug } });
            }
          }

          if (!areasRes && !dmsRes && !membersRes && !unreadRes) {
            toast.error('Failed to load organization data. Please refresh.');
          }

          fetchInbox({ type: 'all', limit: 1 })
            .then((data) => useInboxStore.getState().setCounts(data.counts))
            .catch(() => toast.error('Failed to load inbox counts'));

          // Cross-org, exactly like the badge itself and like the todos.update
          // event that later replaces it — so this is a mount-time seed and is
          // deliberately NOT repeated in the org-switch effect. Scoping it to
          // an org would make the badge jump between two different numbers.
          fetchTodos({ limit: 1 })
            .then((data) => useTodosStore.getState().setBadgeCounts(data.counts))
            .catch(() => toast.error('Failed to load todo counts'));

          // Fetch draft counts for sidebar badge + pencil indicators
          fetchDraftCount()
            .then((data) => useDraftStore.getState().setCount(data.count))
            .catch(() => toast.error('Failed to load draft count'));
          fetchDraftAreaIds()
            .then((data) => useDraftStore.getState().setDraftAreaIds(data.areaIds))
            .catch(() => toast.error('Failed to load draft areas'));

          // Fetch member availability (DND/working hours) for moon indicators
          fetchMemberAvailability(resolvedOrgId)
            .then((data) => useStatusStore.getState().setAvailability(data.availability))
            .catch(() => { /* silent — non-critical */ });
        }
      })
      .catch((err) => {
        console.error('Session initialization failed:', err);
        toast.error('Session expired. Please log in again.');
        logout();
        navigate({ to: '/login' });
      })
      .finally(() => setInitialLoading(false));
  }, [isAuthenticated]);

  // Connect WebSocket — use callback so reconnections always read the latest token
  useEffect(() => {
    if (accessToken) {
      wsClient.connect(() => useAuthStore.getState().accessToken);
      return () => wsClient.disconnect();
    }
  }, [accessToken]);

  // Handle WS connection events
  const handleWsEvent = useCallback(
    (event: WSServerEvent) => {
      const e = event as unknown as { event: string };
      if (e.event === 'connected') {
        setConnected(true);

        // Flush offline queue
        const queue = useChatStore.getState().clearOfflineQueue();
        for (const item of queue) {
          sendMessage(item.orgId, item.areaId, {
            content: item.content,
            replyToMessageId: item.replyToMessageId,
          })
            .then((result: { message: { id: string } }) => {
              useChatStore.getState().replacePendingMessage(item.tempId, result.message as never);
            })
            .catch(() => {
              useChatStore.getState().markPendingFailed(item.tempId);
            });
        }
      } else if (e.event === 'disconnected') {
        setConnected(false);
      } else if (e.event === 'reconnected') {
        // Refetch stale data after reconnection
        if (orgId) {
          fetchUnreadCounts(orgId)
            .then((data) => useUnreadStore.getState().setCounts(data.counts))
            .catch(() => {});
        }
        fetchMyStatus()
          .then((data) => {
            useStatusStore.getState().setMyStatus(
              data.status ? { emoji: data.status.emoji, text: data.status.text, expiresAt: data.status.expiresAt } : null,
            );
          })
          .catch(() => {});
      } else if (event.event === 'presence.update') {
        if (event.status === 'online') {
          usePresenceStore.getState().setOnline(event.userId);
        } else {
          usePresenceStore.getState().setOffline(event.userId);
        }
      } else if (event.event === 'thread.new') {
        useThreadStore.getState().addThread(event.areaId, event.thread);
      } else if (event.event === 'thread.updated') {
        useThreadStore.getState().updateThread(event.threadId, {
          title: event.title,
          isResolved: event.isResolved,
          updatedAt: event.updatedAt,
        });
      } else if (event.event === 'message.new') {
        // Update thread message count if this is a thread message
        if (event.threadId) {
          useThreadStore.getState().incrementThreadMessageCount(event.threadId);
          const msg = event.message as { createdAt?: string };
          if (msg.createdAt) {
            useThreadStore.getState().updateThreadLastActivity(event.threadId, msg.createdAt);
          }
        }
        // Live DM sidebar: refresh the preview text and float the DM to the top
        {
          const msg = event.message as { content?: string | null; authorId?: string; createdAt?: string };
          useOrgStore.getState().updateDmLastMessage(
            event.areaId,
            { content: msg.content ?? null, authorId: msg.authorId, createdAt: msg.createdAt, isDeleted: false },
            true,
          );
        }
        // Increment unread for areas not currently being viewed
        if (event.areaId !== areaId) {
          useUnreadStore.getState().incrementArea(event.areaId);

          // Browser notification when tab not focused
          if (
            typeof Notification !== 'undefined' &&
            Notification.permission === 'granted' &&
            !document.hasFocus()
          ) {
            const msg = event.message as { author?: { displayName?: string }; content?: string | null; id?: string; messageType?: string; voiceMemo?: unknown; attachments?: unknown[] };
            const authorName = msg.author?.displayName ?? 'Someone';
            const areaInfo = useOrgStore.getState().areas.find((a) => a.id === event.areaId);

            // Build title — include thread context if this is a thread message
            let title: string;
            if (areaInfo?.isDm) {
              title = authorName;
            } else {
              const areaName = areaInfo?.name ?? 'chat';
              if (event.threadId) {
                // Look up thread title from the thread store
                const threadsByArea = useThreadStore.getState().threadsByArea;
                const threads = threadsByArea[event.areaId] ?? [];
                const thread = threads.find((t) => t.id === event.threadId);
                const threadTitle = thread?.title ?? 'Thread';
                title = `${authorName} in #${areaName} > ${threadTitle}`;
              } else {
                title = `${authorName} in #${areaName}`;
              }
            }

            // Build body — fallback for voice memos and media-only messages
            let body: string;
            if (msg.voiceMemo || msg.messageType === 'voice') {
              body = 'Sent a voice message';
            } else if (msg.attachments && msg.attachments.length > 0 && !(msg.content ?? '').trim()) {
              body = 'Sent an attachment';
            } else {
              body = (msg.content ?? '').slice(0, 100);
            }

            playNotificationSound();
            const notif = new Notification(title, {
              body,
              tag: `msg-${msg.id}`,
            });
            notif.onclick = () => {
              window.focus();
              const eventAreaSlug = useOrgStore.getState().areaSlugById[event.areaId] ?? event.areaId;
              const currentOrgSlug = params.orgSlug ?? '';
              if (event.threadId) {
                // Navigate to the thread view
                navigateWithTransition(() =>
                  navigate({
                    to: '/org/$orgSlug/$areaSlug/thread/$threadId',
                    params: { orgSlug: currentOrgSlug, areaSlug: eventAreaSlug, threadId: event.threadId! },
                  }),
                );
              } else {
                navigateWithTransition(() =>
                  navigate({
                    to: '/org/$orgSlug/$areaSlug',
                    params: { orgSlug: currentOrgSlug, areaSlug: eventAreaSlug },
                  }),
                );
              }
            };
          }
        }
      } else if (event.event === 'acknowledgment.received') {
        useChatStore.getState().updateAcknowledgment(event.messageId, event.areaId, event.total, event.acknowledgedCount);
        // Announcements render inside threads too — keep those live as well
        useThreadStore.getState().updateThreadMessageFields(event.messageId, {
          acknowledgmentSummary: { total: event.total, acknowledgedCount: event.acknowledgedCount } as never,
        });
      } else if (event.event === 'pin.added') {
        useChatStore.getState().togglePinned(event.messageId, event.areaId, true);
        useThreadStore.getState().updateThreadMessageFields(event.messageId, { isPinned: true });
        useChatStore.getState().bumpPinsVersion(event.areaId);
      } else if (event.event === 'pin.removed') {
        useChatStore.getState().togglePinned(event.messageId, event.areaId, false);
        useThreadStore.getState().updateThreadMessageFields(event.messageId, { isPinned: false });
        useChatStore.getState().bumpPinsVersion(event.areaId);
      } else if (event.event === 'comment.new') {
        // Comments are delivered per-user when internal, so simply receiving
        // this event means the server decided this user may see it — no
        // client-side audience check to get wrong.
        const myId = useAuthStore.getState().user?.id;
        useChatStore.getState().applyIncomingComment(
          event.areaId,
          event.parentMessageId,
          event.threadId,
          event.isInternal,
          event.comment.author.userId === myId,
        );
      } else if (event.event === 'message.edited') {
        // Keep every cached surface live — the open area handles its own copy,
        // but other cached areas and DM previews went stale until refetch
        useChatStore.getState().updateMessageContent(event.messageId, event.areaId, event.content, event.editedAt);
        useOrgStore.getState().updateDmLastMessage(event.areaId, { content: event.content });
      } else if (event.event === 'message.deleted') {
        useChatStore.getState().markMessageDeleted(event.messageId, event.areaId);
        useOrgStore.getState().updateDmLastMessage(event.areaId, { isDeleted: true, content: null });
      } else if (event.event === 'link_preview.ready') {
        useChatStore.getState().setLinkPreviews(event.messageId, event.areaId, event.previews);
        // URLs pasted inside threads get their async previews too
        useThreadStore.getState().updateThreadMessageFields(event.messageId, { linkPreviews: event.previews });
      } else if (event.event === 'status.updated') {
        useStatusStore.getState().setUserStatus(event.userId, event.status);
      } else if (event.event === 'status.cleared') {
        useStatusStore.getState().clearUserStatus(event.userId);
      } else if (event.event === 'inbox.update') {
        // Refetch unread counts
        if (orgId) {
          fetchUnreadCounts(orgId)
            .then((data) => useUnreadStore.getState().setCounts(data.counts))
            .catch(() => toast.error('Failed to load unread counts'));
        }
        // Update inbox store counts from WS event
        if (event.unreadCounts) {
          useInboxStore.getState().setCounts({
            unread: event.unreadCounts.total ?? 0,
            acknowledgmentsPending: event.unreadCounts.acknowledgmentsPending ?? 0,
            replyLater: event.unreadCounts.replyLater ?? 0,
          });
        }
      } else if (event.event === 'todos.update') {
        // Carries cross-org counts only, never which row moved — the store
        // bumps a revision and any mounted Todos view refetches itself.
        applyTodosUpdateEvent(event);
      } else if (event.event === 'typing.update') {
        // Store typing state for all areas (enables DM typing in sidebar)
        useChatStore.getState().setTypers(event.areaId, event.typers);
      } else if ((event as any).event === 'poll.voted' || (event as any).event === 'poll.closed') {
        // Update poll data on messages in chat store
        const pollEvent = event as any;
        if (pollEvent.messageId && pollEvent.areaId && pollEvent.poll) {
          useChatStore.getState().updateMessageInArea(pollEvent.areaId, pollEvent.messageId, { poll: pollEvent.poll });
          // Polls inside threads previously froze at their initial tallies
          useThreadStore.getState().updateThreadMessageFields(pollEvent.messageId, { poll: pollEvent.poll });
        }
      }
    },
    [setConnected, orgId, areaId, navigate, params.orgSlug],
  );

  useWebSocket(handleWsEvent);

  // Subscribe to DM areas for typing indicators in sidebar
  const dmAreaIds = useMemo(() => dms.map((d) => d.id), [dms]);
  useSubscribeAreas(dmAreaIds);

  // Proactive token refresh — refresh at ~12min of 15min lifetime
  useEffect(() => {
    if (!isAuthenticated) return;
    const REFRESH_INTERVAL = 12 * 60 * 1000; // 12 minutes
    const doRefresh = () => {
      // Single-flight via the api client — a raw refresh racing the client's
      // internal one double-spends the token and the server logs us out
      // everywhere (token-theft detection)
      void apiClient.refresh().catch(() => {});
    };
    const timer = setInterval(doRefresh, REFRESH_INTERVAL);
    return () => clearInterval(timer);
  }, [isAuthenticated]);

  // Trigger onboarding tour on first login if not completed
  useEffect(() => {
    if (!isAuthenticated || initialLoading) return;
    const { hasCompletedOnboarding, startTour } = useOnboardingStore.getState();
    if (!hasCompletedOnboarding) {
      // Small delay to ensure sidebar has rendered with data-tour elements
      const timer = setTimeout(() => startTour(), 1000);
      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, initialLoading]);

  // Refresh stale token when user returns to a sleeping tab
  useEffect(() => {
    if (!isAuthenticated) return;
    const handleFocus = () => {
      // Same single-flight rule as the interval refresher above
      void apiClient.refresh().catch(() => {});
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [isAuthenticated]);

  // Request browser notification permission
  // Notification permission is requested on the user's first message send
  // (message-input) — an unprompted on-load request gets reflexively blocked
  // and permanently kills notifications.

  // Close mobile sidebar on route navigation
  useEffect(() => {
    setSidebarOpen(false);
  }, [params.orgSlug, params.areaSlug]);

  // Track selected org from URL and load areas + DMs + unread counts (org switching after initial load)
  useEffect(() => {
    if (initialLoading) return; // Initial load already handled above
    if (orgId && orgId !== selectedOrgId) {
      setSelectedOrg(orgId);
      setOrgSwitching(true);

      // Clear stale data from previous org immediately so slug maps don't cross-pollinate
      setAreas([]);
      setDms([]);
      setMembers([]);
      setCounts({});

      // Persist last selected org
      const slug = useOrgStore.getState().orgSlugById[orgId];
      if (slug) localStorage.setItem(LAST_ORG_KEY, slug);

      // Await all fetches before rendering child routes (prevents stale slug lookups)
      let fetchFailed = false;
      Promise.all([
        fetchAreas(orgId).catch((e) => { console.error('Failed to load areas on org switch:', e); fetchFailed = true; return { areas: [] as OrgArea[] }; }),
        fetchDms(orgId).catch((e) => { console.error('Failed to load DMs on org switch:', e); fetchFailed = true; return { dms: [] as DmPreview[] }; }),
        fetchOrgMembers(orgId).catch((e) => { console.error('Failed to load members on org switch:', e); fetchFailed = true; return { members: [] as OrgMember[] }; }),
        fetchUnreadCounts(orgId).catch((e) => { console.error('Failed to load unread counts on org switch:', e); fetchFailed = true; return { counts: {} }; }),
      ]).then(([areasRes, dmsRes, membersRes, unreadRes]) => {
        setAreas(areasRes.areas);
        setDms(dmsRes.dms);
        setMembers(membersRes.members);
        setCounts(unreadRes.counts);
        if (fetchFailed) {
          toast.error('Some data failed to load. Please refresh if things look wrong.');
        }
      }).finally(() => setOrgSwitching(false));

      // Fetch inbox counts for badge (not blocking)
      fetchInbox({ type: 'all', limit: 1 })
        .then((data) => useInboxStore.getState().setCounts(data.counts))
        .catch(() => toast.error('Failed to load inbox counts'));

      // Fetch draft counts for sidebar badge + pencil indicators
      fetchDraftCount()
        .then((data) => useDraftStore.getState().setCount(data.count))
        .catch(() => toast.error('Failed to load draft count'));
      fetchDraftAreaIds()
        .then((data) => useDraftStore.getState().setDraftAreaIds(data.areaIds))
        .catch(() => toast.error('Failed to load draft areas'));

      // Fetch member availability (DND/working hours) for moon indicators
      fetchMemberAvailability(orgId)
        .then((data) => useStatusStore.getState().setAvailability(data.availability))
        .catch(() => { /* silent — non-critical */ });
    }
  }, [orgId, initialLoading]);

  // Fetch my status and notification settings on mount
  const myStatus = useStatusStore((s) => s.myStatus);
  const myNotificationSettings = useStatusStore((s) => s.myNotificationSettings);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [dndDialogOpen, setDndDialogOpen] = useState(false);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [editProfileDialogOpen, setEditProfileDialogOpen] = useState(false);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [createAreaOpen, setCreateAreaOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(true);

  const isGuest = user?.isGuest === true;
  const isOnline = useIsOnline();

  useEffect(() => {
    if (!isAuthenticated) return;
    fetchMyStatus()
      .then((data) => {
        useStatusStore.getState().setMyStatus(
          data.status ? { emoji: data.status.emoji, text: data.status.text, expiresAt: data.status.expiresAt } : null,
        );
      })
      .catch(() => toast.error('Failed to load status'));
    fetchNotificationSettings()
      .then((data) => {
        useStatusStore.getState().setMyNotificationSettings({
          dndUntil: data.settings.dndUntil,
          workingHoursEnabled: data.settings.workingHoursEnabled,
          workingHours: data.settings.workingHours,
          timezone: data.settings.timezone,
          emailFallbackEnabled: data.settings.emailFallbackEnabled,
          digestEnabled: data.settings.digestEnabled,
        });
      })
      .catch(() => toast.error('Failed to load notification settings'));
  }, [isAuthenticated]);

  // Refresh member availability every 5 minutes (DND expires, working hours change)
  useEffect(() => {
    if (!orgId) return;
    const AVAILABILITY_REFRESH_MS = 5 * 60 * 1000;
    const interval = setInterval(() => {
      fetchMemberAvailability(orgId)
        .then((data) => useStatusStore.getState().setAvailability(data.availability))
        .catch(() => { /* silent */ });
    }, AVAILABILITY_REFRESH_MS);
    return () => clearInterval(interval);
  }, [orgId]);

  const [uploadingPicture, setUploadingPicture] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleProfilePictureUpload = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be under 5MB');
      return;
    }

    setUploadingPicture(true);
    try {
      // 1. Get presigned upload URL
      const { uploadUrl, fileKey } = await presignProfilePicture(file.name, file.type, file.size);

      // 2. Upload to S3
      const uploadRes = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      if (!uploadRes.ok) {
        throw new Error(`S3 upload failed: ${uploadRes.status} ${uploadRes.statusText}`);
      }

      // 3. Complete the upload (generates thumbnail, updates user)
      const { profilePictureUrl } = await completeProfilePicture(fileKey);

      // 4. Update user in auth store
      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        const updatedUser = { ...currentUser, profilePictureUrl };
        const at = useAuthStore.getState().accessToken!;
        const rt = useAuthStore.getState().refreshToken!;
        useAuthStore.getState().setAuth(updatedUser, at, rt);
      }
    } catch (err) {
      toast.error('Failed to upload profile picture');
    } finally {
      setUploadingPicture(false);
    }
  }, []);

  const isDndActive = myNotificationSettings?.dndUntil && new Date(myNotificationSettings.dndUntil) > new Date();

  // Sort orgs for the switcher (by sortOrder if available, otherwise stable order)
  const sortedOrgs = useMemo(
    () =>
      [...orgs].sort((a, b) => {
        const aOrder = (a as unknown as { sortOrder?: number }).sortOrder ?? Number.MAX_SAFE_INTEGER;
        const bOrder = (b as unknown as { sortOrder?: number }).sortOrder ?? Number.MAX_SAFE_INTEGER;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return a.name.localeCompare(b.name);
      }),
    [orgs],
  );

  const currentOrg = orgs.find((o) => o.id === (orgId ?? selectedOrgId));
  const currentOrgSlug = currentOrg ? (orgSlugById[currentOrg.id] ?? currentOrg.slug) : undefined;

  // Split areas into member areas and joinable, memoized to avoid re-creating arrays every render
  const memberAreas = useMemo(
    () => sortAreas(areas.filter((a) => a.isMember && !a.isArchived && !a.isDm)),
    [areas],
  );
  const persistentAreas = useMemo(() => memberAreas.filter((a) => a.type === 'persistent'), [memberAreas]);
  const projectAreas = useMemo(() => memberAreas.filter((a) => a.type === 'project'), [memberAreas]);
  const joinableAreas = useMemo(
    () => sortAreas(areas.filter((a) => !a.isMember && !a.isArchived && !a.isDm)),
    [areas],
  );

  // Aggregate unread counts for section headings
  const totalAreaUnread = useMemo(
    () => memberAreas.reduce((sum, a) => sum + (countsByArea[a.id] ?? 0), 0),
    [memberAreas, countsByArea],
  );
  const totalDmUnread = useMemo(
    () => dms.reduce((sum, d) => sum + (countsByArea[d.id] ?? 0), 0),
    [dms, countsByArea],
  );

  const isAdmin = currentOrg && (currentOrg.role === 'owner' || currentOrg.role === 'admin');

  // Drag-and-drop sensors
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !currentOrg) return;

      // Read current areas from store to avoid stale closures
      const currentAreas = useOrgStore.getState().areas;
      const sorted = sortAreas(currentAreas.filter((a) => a.isMember && !a.isArchived && !a.isDm));
      const pAreas = sorted.filter((a) => a.type === 'persistent');
      const prAreas = sorted.filter((a) => a.type === 'project');

      // Find which group the dragged item is in
      const isPersistent = pAreas.some((a) => a.id === active.id);
      const group = isPersistent ? pAreas : prAreas;

      const oldIndex = group.findIndex((a) => a.id === active.id);
      const newIndex = group.findIndex((a) => a.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      // Reorder within the group
      const reordered = [...group];
      const [moved] = reordered.splice(oldIndex, 1);
      reordered.splice(newIndex, 0, moved);

      // Build full sorted list: persistent first, then project
      const allSorted = isPersistent
        ? [...reordered, ...prAreas]
        : [...pAreas, ...reordered];
      const areaIds = allSorted.map((a) => a.id);

      // Optimistic update
      const updatedAreas = currentAreas.map((a) => {
        const idx = areaIds.indexOf(a.id);
        return idx !== -1 ? { ...a, sortOrder: idx } : a;
      });
      setAreas(updatedAreas);

      // Persist
      updateAreaSortOrder(currentOrg.id, areaIds).catch(() => {
        toast.error('Failed to save area order');
        // Revert on failure
        fetchAreas(currentOrg.id).then((data) => setAreas(data.areas));
      });
    },
    [currentOrg, setAreas],
  );

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept when user is typing in an input/textarea
      const tag = (e.target as HTMLElement)?.tagName;
      const isEditing = tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable;

      // Escape — close sidebar on mobile
      if (e.key === 'Escape') {
        if (sidebarOpen) {
          e.preventDefault();
          setSidebarOpen(false);
        }
        return;
      }

      // Cmd+K / Ctrl+K — open command palette
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen((prev) => !prev);
        return;
      }

      // Don't process further shortcuts when editing
      if (isEditing) return;

      // ? — open help page
      if (e.key === '?' && currentOrgSlug) {
        e.preventDefault();
        navigateWithTransition(() =>
          navigate({ to: '/org/$orgSlug/help', params: { orgSlug: currentOrgSlug } }),
        );
        return;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [sidebarOpen, currentOrgSlug, navigate]);

  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next)); } catch {}
      return next;
    });
  }, []);

  // Show spinner while initial data loads (slug maps must be populated before rendering)
  if (initialLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-500" />
      </div>
    );
  }

  return (
    <div className="flex h-full">
      {/* Skip to content */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-[100] focus:rounded-lg focus:bg-blue-600 focus:px-4 focus:py-2 focus:text-white focus:shadow-lg"
      >
        Skip to content
      </a>
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}
      {/* Sidebar */}
      <aside ref={sidebarFocusTrapRef} role="navigation" aria-label="Sidebar" className={`${sidebarOpen ? 'fixed inset-y-0 left-0 z-50 flex w-64' : 'hidden'} flex-col border-r border-gray-200 bg-gray-50 transition-all duration-200 ease-in-out lg:relative lg:flex dark:border-gray-800 dark:bg-gray-900 ${!sidebarOpen ? (sidebarCollapsed ? 'lg:w-12' : 'lg:w-64') : ''}`}>
        {/* Org switcher — hidden for guests */}
        {isGuest ? (
          <div className="flex h-14 items-center border-b border-gray-200 px-3 dark:border-gray-800">
            {sidebarCollapsed ? (
              <div className="hidden w-full items-center justify-center lg:flex">
                <Tooltip label={currentOrg?.name ?? 'Guest'} position="right">
                  {currentOrg ? <OrgIcon org={currentOrg} /> : <span className="text-sm font-semibold text-gray-600">G</span>}
                </Tooltip>
              </div>
            ) : null}
            <div className={`flex items-center gap-2 px-2 py-1.5 ${sidebarCollapsed ? 'lg:hidden' : ''}`}>
              {currentOrg ? (
                <>
                  <OrgIcon org={currentOrg} />
                  <span className="text-sm font-semibold">{currentOrg.name}</span>
                </>
              ) : (
                <span className="text-sm font-semibold text-gray-600">Guest Access</span>
              )}
              <span className="rounded bg-gray-200 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">Guest</span>
            </div>
          </div>
        ) : (
          <div className="flex h-14 items-center border-b border-gray-200 px-3 dark:border-gray-800">
            {sidebarCollapsed ? (
              <div className="hidden w-full items-center justify-center lg:flex">
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger className="rounded-lg p-1 transition-colors hover:bg-gray-200 dark:hover:bg-gray-800">
                    {currentOrg ? <OrgIcon org={currentOrg} /> : <span className="text-sm font-semibold text-gray-600">?</span>}
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Portal>
                    <DropdownMenu.Content
                      className="z-50 min-w-[200px] rounded-lg border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-gray-900"
                      sideOffset={4}
                      side="right"
                      align="start"
                    >
                      <DropdownMenu.Label className="px-2 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                        Your organizations
                      </DropdownMenu.Label>
                      {sortedOrgs.map((org) => (
                        <DropdownMenu.Item
                          key={org.id}
                          className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                          onSelect={() => navigateWithTransition(() => navigate({ to: '/org/$orgSlug', params: { orgSlug: org.slug } }))}
                        >
                          <OrgIcon org={org} />
                          <span className="flex-1 truncate">{org.name}</span>
                          {org.id === currentOrg?.id ? (
                            <svg className="h-4 w-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          ) : null}
                        </DropdownMenu.Item>
                      ))}
                    </DropdownMenu.Content>
                  </DropdownMenu.Portal>
                </DropdownMenu.Root>
              </div>
            ) : null}
            <DropdownMenu.Root>
              <DropdownMenu.Trigger className={`flex flex-1 items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-gray-200 dark:hover:bg-gray-800 ${sidebarCollapsed ? 'lg:hidden' : ''}`}>
                {currentOrg ? (
                  <>
                    <OrgIcon org={currentOrg} />
                    <span className="flex-1 truncate text-sm font-semibold">{currentOrg.name}</span>
                  </>
                ) : (
                  <span className="text-sm font-semibold text-gray-600">Select org</span>
                )}
                <ChevronDown />
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  className="z-50 min-w-[200px] rounded-lg border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-gray-900"
                  sideOffset={4}
                  align="start"
                >
                  <DropdownMenu.Label className="px-2 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                    Your organizations
                  </DropdownMenu.Label>
                  {sortedOrgs.map((org, idx) => (
                    <DropdownMenu.Item
                      key={org.id}
                      className="group flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                      onSelect={() => navigateWithTransition(() => navigate({ to: '/org/$orgSlug', params: { orgSlug: org.slug } }))}
                    >
                      {orgs.length > 1 && (
                        <span className="flex flex-col opacity-0 group-hover:opacity-60">
                          <button
                            className="text-gray-400 hover:text-gray-600 disabled:opacity-0"
                            disabled={idx === 0}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              const ids = sortedOrgs.map((o) => o.id);
                              [ids[idx - 1], ids[idx]] = [ids[idx], ids[idx - 1]];
                              const updatedOrgs = orgs.map((o) => {
                                const i = ids.indexOf(o.id);
                                return i !== -1 ? { ...o, sortOrder: i } : o;
                              });
                              setOrgs(updatedOrgs);
                              updateOrgSortOrder(ids).catch(() => toast.error('Failed to save org order'));
                            }}
                          >
                            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" /></svg>
                          </button>
                          <button
                            className="text-gray-400 hover:text-gray-600 disabled:opacity-0"
                            disabled={idx === sortedOrgs.length - 1}
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              const ids = sortedOrgs.map((o) => o.id);
                              [ids[idx], ids[idx + 1]] = [ids[idx + 1], ids[idx]];
                              const updatedOrgs = orgs.map((o) => {
                                const i = ids.indexOf(o.id);
                                return i !== -1 ? { ...o, sortOrder: i } : o;
                              });
                              setOrgs(updatedOrgs);
                              updateOrgSortOrder(ids).catch(() => toast.error('Failed to save org order'));
                            }}
                          >
                            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                          </button>
                        </span>
                      )}
                      <OrgIcon org={org} />
                      <span className="flex-1 truncate">{org.name}</span>
                      {org.id === currentOrg?.id ? (
                        <svg className="h-4 w-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : null}
                    </DropdownMenu.Item>
                  ))}
                  <DropdownMenu.Separator className="my-1 h-px bg-gray-200 dark:bg-gray-700" />
                  <DropdownMenu.Item
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-gray-600 outline-none transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                    onSelect={() => navigateWithTransition(() => navigate({ to: '/org/new' }))}
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                    Create organization
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
            {/* Settings gear icon for admin/owner */}
            {isAdmin && currentOrgSlug && !sidebarCollapsed && (
              <Link
                to="/org/$orgSlug/settings"
                params={{ orgSlug: currentOrgSlug }}
                className="ml-1 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                title="Organization settings"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </Link>
            )}
          </div>
        )}

        {/* Nav */}
        <nav className={`flex-1 overflow-y-auto p-3 ${sidebarCollapsed ? 'lg:px-1 lg:py-2' : ''}`}>
          {currentOrg && currentOrgSlug ? (
            <div className={sidebarCollapsed ? 'lg:space-y-2 space-y-4' : 'space-y-4'}>
              {/* Tools cluster — Inbox, Search and friends sit at the TOP of the
                  nav (user directive, matching Quo). The fuller left-rail
                  restructure is Phase E; this is the ordering change only. */}
              <div className="space-y-1">
                {!isGuest && (
                  <>
                    {/* Collapsed nav icons — hidden on mobile, shown on lg+ when collapsed */}
                    {sidebarCollapsed && (
                      <div className="hidden lg:block space-y-0.5">
                        <Tooltip label={`Inbox${(() => { const t = inboxCounts.unread + inboxCounts.acknowledgmentsPending + inboxCounts.replyLater; return t > 0 ? ` (${t})` : ''; })()}`} position="right">
                          <Link
                            to="/org/$orgSlug/inbox"
                            params={{ orgSlug: currentOrgSlug }}
                            className="relative flex items-center justify-center rounded-lg p-2 text-gray-700 transition-colors hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-800 [&.active]:bg-blue-50 [&.active]:text-blue-700 dark:[&.active]:bg-blue-900/40 dark:[&.active]:text-blue-300"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 005.586 13H4" />
                            </svg>
                            {(() => {
                              const total = inboxCounts.unread + inboxCounts.acknowledgmentsPending + inboxCounts.replyLater;
                              return total > 0 ? (
                                <span className="absolute -right-0.5 -top-0.5 min-w-[14px] rounded-full bg-blue-500 px-1 text-center text-[8px] font-bold leading-[14px] text-white">
                                  {total > 9 ? '9+' : total}
                                </span>
                              ) : null;
                            })()}
                          </Link>
                        </Tooltip>
                        <Tooltip label="Bookmarks" position="right">
                          <Link
                            to="/org/$orgSlug/bookmarks"
                            params={{ orgSlug: currentOrgSlug }}
                            className="flex items-center justify-center rounded-lg p-2 text-gray-700 transition-colors hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-800 [&.active]:bg-blue-50 [&.active]:text-blue-700 dark:[&.active]:bg-blue-900/40 dark:[&.active]:text-blue-300"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                            </svg>
                          </Link>
                        </Tooltip>
                        <Tooltip label={`Drafts${draftCount > 0 ? ` (${draftCount})` : ''}`} position="right">
                          <Link
                            to="/org/$orgSlug/drafts"
                            params={{ orgSlug: currentOrgSlug }}
                            className="relative flex items-center justify-center rounded-lg p-2 text-gray-700 transition-colors hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-800 [&.active]:bg-blue-50 [&.active]:text-blue-700 dark:[&.active]:bg-blue-900/40 dark:[&.active]:text-blue-300"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
                            </svg>
                            {draftCount > 0 && (
                              <span className="absolute -right-0.5 -top-0.5 min-w-[14px] rounded-full bg-gray-400 px-1 text-center text-[8px] font-bold leading-[14px] text-white dark:bg-gray-600">
                                {draftCount > 9 ? '9+' : draftCount}
                              </span>
                            )}
                          </Link>
                        </Tooltip>
                        <Tooltip label="Scheduled" position="right">
                          <Link
                            to="/org/$orgSlug/scheduled-messages"
                            params={{ orgSlug: currentOrgSlug }}
                            className="flex items-center justify-center rounded-lg p-2 text-gray-700 transition-colors hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-800 [&.active]:bg-blue-50 [&.active]:text-blue-700 dark:[&.active]:bg-blue-900/40 dark:[&.active]:text-blue-300"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </Link>
                        </Tooltip>
                        <Tooltip label="Members" position="right">
                          <Link
                            to="/org/$orgSlug/members"
                            params={{ orgSlug: currentOrgSlug }}
                            className="flex items-center justify-center rounded-lg p-2 text-gray-700 transition-colors hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-800 [&.active]:bg-blue-50 [&.active]:text-blue-700 dark:[&.active]:bg-blue-900/40 dark:[&.active]:text-blue-300"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-1.076M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                            </svg>
                          </Link>
                        </Tooltip>
                      </div>
                    )}
                    {/*
                      Inbox is one of the three surfaces (Chats · Inbox · Todos,
                      CLAUDE.md "Product direction"), so it does not belong in a
                      collapsible "Tools" drawer with Drafts and Bookmarks — the
                      rail said it was an accessory. It sits above them now and
                      cannot be collapsed away.

                      Todos sits directly under Inbox: the two are the "what do
                      I owe" pair, and separating them would put a surface
                      behind a disclosure triangle again.
                    */}
                    <div className={`mb-3 space-y-0.5 ${sidebarCollapsed ? 'lg:hidden' : ''}`}>
                    <Link
                      to="/org/$orgSlug/inbox"
                      params={{ orgSlug: currentOrgSlug }}
                      data-tour="inbox"
                      className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-800 [&.active]:bg-blue-50 [&.active]:text-blue-700 dark:[&.active]:bg-blue-900/40 dark:[&.active]:text-blue-300"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 005.586 13H4" />
                      </svg>
                      Inbox
                      {(() => {
                        const total = inboxCounts.unread + inboxCounts.acknowledgmentsPending + inboxCounts.replyLater;
                        return total > 0 ? (
                          <span className="ml-auto min-w-[20px] rounded-full bg-blue-500 px-1.5 py-0.5 text-center text-[10px] font-bold text-white">
                            {total > 99 ? '99+' : total}
                          </span>
                        ) : null;
                      })()}
                    </Link>
                    <Link
                      to="/org/$orgSlug/todos"
                      params={{ orgSlug: currentOrgSlug }}
                      data-tour="todos"
                      className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-800 [&.active]:bg-blue-50 [&.active]:text-blue-700 dark:[&.active]:bg-blue-900/40 dark:[&.active]:text-blue-300"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Todos
                      {todoCount > 0 && (
                        <span className="ml-auto min-w-[20px] rounded-full bg-blue-500 px-1.5 py-0.5 text-center text-[10px] font-bold text-white">
                          {todoCount > 99 ? '99+' : todoCount}
                        </span>
                      )}
                    </Link>
                    </div>
                    {/* Expanded Tools group — always shown on mobile, hidden on lg+ when collapsed */}
                    <div className={sidebarCollapsed ? 'lg:hidden' : ''}>
                    <button
                      onClick={() => setToolsOpen((v) => !v)}
                      className="mb-1 flex w-full items-center gap-1.5 px-3 text-left"
                      aria-expanded={toolsOpen}
                    >
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                        Tools
                      </h3>
                      <svg className={`h-3 w-3 text-gray-400 transition-transform ${toolsOpen ? '' : '-rotate-90'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {toolsOpen && (
                    <div className="space-y-0.5">
                    <Link
                      to="/org/$orgSlug/bookmarks"
                      params={{ orgSlug: currentOrgSlug }}
                      className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-800 [&.active]:bg-blue-50 [&.active]:text-blue-700 dark:[&.active]:bg-blue-900/40 dark:[&.active]:text-blue-300"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                      </svg>
                      Bookmarks
                    </Link>
                    <Link
                      to="/org/$orgSlug/drafts"
                      params={{ orgSlug: currentOrgSlug }}
                      className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-800 [&.active]:bg-blue-50 [&.active]:text-blue-700 dark:[&.active]:bg-blue-900/40 dark:[&.active]:text-blue-300"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
                      </svg>
                      Drafts
                      {draftCount > 0 && (
                        <span className="ml-auto min-w-[20px] rounded-full bg-gray-400 px-1.5 py-0.5 text-center text-[10px] font-bold text-white dark:bg-gray-600">
                          {draftCount > 99 ? '99+' : draftCount}
                        </span>
                      )}
                    </Link>
                    <Link
                      to="/org/$orgSlug/scheduled-messages"
                      params={{ orgSlug: currentOrgSlug }}
                      className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-800 [&.active]:bg-blue-50 [&.active]:text-blue-700 dark:[&.active]:bg-blue-900/40 dark:[&.active]:text-blue-300"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Scheduled
                    </Link>
                    <Link
                      to="/org/$orgSlug/members"
                      params={{ orgSlug: currentOrgSlug }}
                      className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-800 [&.active]:bg-blue-50 [&.active]:text-blue-700 dark:[&.active]:bg-blue-900/40 dark:[&.active]:text-blue-300"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-1.076M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                      </svg>
                      Members
                    </Link>
                    </div>
                    )}
                    </div>
                  </>
                )}
              </div>
              {/* Areas section with drag/drop */}
              {sidebarCollapsed ? (
                /* Collapsed areas — icon only with tooltip, hidden on mobile */
                <div className="hidden lg:block">
                  <div className="mb-1 flex items-center justify-center">
                    {totalAreaUnread > 0 && (
                      <span className="min-w-[14px] rounded-full bg-blue-500 px-1 text-center text-[8px] font-bold leading-[14px] text-white">
                        {totalAreaUnread > 9 ? '9+' : totalAreaUnread}
                      </span>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    {persistentAreas.map((area) => {
                      const unread = countsByArea[area.id] ?? 0;
                      const muted = areaNotificationLevels[area.id] === 'muted';
                      return (
                        <Tooltip key={area.id} label={`${area.name}${muted ? ' (Muted)' : ''}${unread > 0 ? ` (${unread})` : ''}`} position="right">
                          <Link
                            to="/org/$orgSlug/$areaSlug"
                            params={{ orgSlug: currentOrgSlug, areaSlug: area.slug }}
                            className={`relative flex items-center justify-center rounded-lg p-1.5 transition-colors hover:bg-gray-200 dark:hover:bg-gray-800 [&.active]:bg-blue-50 [&.active]:text-blue-700 dark:[&.active]:bg-blue-900/40 dark:[&.active]:text-blue-300 ${muted ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}
                          >
                            <AreaIcon icon={area.icon} iconColor={area.iconColor} className={`h-4 w-4 ${muted ? 'opacity-50' : ''}`} />
                            {unread > 0 && (
                              <span className="absolute -right-0.5 -top-0.5 min-w-[8px] rounded-full bg-blue-500 p-0.5" />
                            )}
                          </Link>
                        </Tooltip>
                      );
                    })}
                    {projectAreas.map((area) => {
                      const unread = countsByArea[area.id] ?? 0;
                      const muted = areaNotificationLevels[area.id] === 'muted';
                      return (
                        <Tooltip key={area.id} label={`${area.name}${muted ? ' (Muted)' : ''}${unread > 0 ? ` (${unread})` : ''}`} position="right">
                          <Link
                            to="/org/$orgSlug/$areaSlug"
                            params={{ orgSlug: currentOrgSlug, areaSlug: area.slug }}
                            className={`relative flex items-center justify-center rounded-lg p-1.5 transition-colors hover:bg-gray-200 dark:hover:bg-gray-800 [&.active]:bg-blue-50 [&.active]:text-blue-700 dark:[&.active]:bg-blue-900/40 dark:[&.active]:text-blue-300 ${muted ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}
                          >
                            <AreaIcon icon={area.icon} iconColor={area.iconColor} className={`h-4 w-4 ${muted ? 'opacity-50' : ''}`} />
                            {unread > 0 && (
                              <span className="absolute -right-0.5 -top-0.5 min-w-[8px] rounded-full bg-blue-500 p-0.5" />
                            )}
                          </Link>
                        </Tooltip>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              {/* Expanded areas — always on mobile, conditional on desktop */}
              <div className={sidebarCollapsed ? 'lg:hidden' : ''} data-tour="areas">
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                {/* Persistent areas */}
                <div>
                  <div className="mb-1 flex items-center gap-1.5 px-3">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                      Rooms
                    </h3>
                    {totalAreaUnread > 0 && (
                      <span className="min-w-[18px] rounded-full bg-blue-500 px-1 py-0.5 text-center text-[9px] font-bold leading-none text-white">
                        {totalAreaUnread > 99 ? '99+' : totalAreaUnread}
                      </span>
                    )}
                    {isAdmin && (
                      <button
                        onClick={() => setCreateAreaOpen(true)}
                        className="ml-auto rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                        title="Create room"
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    )}
                  </div>
                  <SortableContext items={persistentAreas.map((a) => a.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-0.5">
                      {persistentAreas.map((area) => (
                        <SortableAreaItem
                          key={area.id}
                          area={area}
                          orgSlug={currentOrgSlug}
                          unread={countsByArea[area.id] ?? 0}
                          hasDraft={draftAreaIds.has(area.id)}
                          isMuted={areaNotificationLevels[area.id] === 'muted'}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </div>

                {/* Project areas */}
                {projectAreas.length > 0 && (
                  <div className="mt-3">
                    <h3 className="mb-1 px-3 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                      Projects
                    </h3>
                    <SortableContext items={projectAreas.map((a) => a.id)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-0.5">
                        {projectAreas.map((area) => (
                          <SortableAreaItem
                            key={area.id}
                            area={area}
                            orgSlug={currentOrgSlug}
                            unread={countsByArea[area.id] ?? 0}
                            hasDraft={draftAreaIds.has(area.id)}
                            isMuted={areaNotificationLevels[area.id] === 'muted'}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </div>
                )}
              </DndContext>
              </div>

              {/* Browse joinable areas — hidden when collapsed */}
              {joinableAreas.length > 0 && (
                <div className={sidebarCollapsed ? 'lg:hidden' : ''}>
                  <div className="mt-1 border-t border-gray-200 pt-1 dark:border-gray-700">
                    <p className="px-3 py-1 text-xs text-gray-400">Browse rooms</p>
                    {joinableAreas.map((area) => (
                      <button
                        key={area.id}
                        onClick={async () => {
                          const { joinArea } = await import('@/lib/api');
                          await joinArea(currentOrg.id, area.id);
                          const data = await fetchAreas(currentOrg.id);
                          setAreas(data.areas);
                          navigateWithTransition(() =>
                            navigate({
                              to: '/org/$orgSlug/$areaSlug',
                              params: { orgSlug: currentOrgSlug, areaSlug: area.slug },
                            }),
                          );
                        }}
                        className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-left text-sm text-gray-600 transition-colors hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-800"
                      >
                        <AreaIcon icon={area.icon} iconColor={area.iconColor} className="h-4 w-4" />
                        <span className="flex-1 truncate">{area.name}</span>
                        <span className="text-xs text-blue-500">Join</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* DMs section — hidden for guests */}
              {!isGuest && (
                <>
                  {/* Collapsed DMs — avatar initials only */}
                  {sidebarCollapsed && (
                    <div className="hidden lg:block">
                      <div className="mb-1 flex items-center justify-center">
                        <Tooltip label={`Messages${totalDmUnread > 0 ? ` (${totalDmUnread})` : ''}`} position="right">
                          <Link
                            to="/org/$orgSlug/messages"
                            params={{ orgSlug: currentOrgSlug }}
                            className="relative rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                            </svg>
                            {totalDmUnread > 0 && (
                              <span className="absolute -right-0.5 -top-0.5 min-w-[8px] rounded-full bg-blue-500 p-0.5" />
                            )}
                          </Link>
                        </Tooltip>
                      </div>
                      <div className="space-y-0.5">
                        {[...dms].sort((a, b) => {
                          if (!a.lastMessage && !b.lastMessage) return 0;
                          if (!a.lastMessage) return 1;
                          if (!b.lastMessage) return -1;
                          return new Date(b.lastMessage.createdAt).getTime() - new Date(a.lastMessage.createdAt).getTime();
                        }).slice(0, 5).map((dm) => {
                          const unread = countsByArea[dm.id] ?? 0;
                          const otherParticipants = dm.participants.filter((p) => p.userId !== user?.id);
                          const dmDisplayName = otherParticipants.map((p) => p.displayName).join(', ') || dm.name;
                          const firstParticipant = otherParticipants[0] ?? dm.participants[0];
                          return (
                            <Tooltip key={dm.id} label={`${dmDisplayName}${unread > 0 ? ` (${unread})` : ''}`} position="right">
                              <Link
                                to="/org/$orgSlug/$areaSlug"
                                params={{ orgSlug: currentOrgSlug, areaSlug: dm.id }}
                                className="relative flex items-center justify-center rounded-lg p-1 transition-colors hover:bg-gray-200 dark:hover:bg-gray-800 [&.active]:bg-blue-50 [&.active]:text-blue-700 dark:[&.active]:bg-blue-900/40 dark:[&.active]:text-blue-300"
                              >
                                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                  {firstParticipant?.displayName?.charAt(0).toUpperCase() ?? '?'}
                                </div>
                                {unread > 0 && (
                                  <span className="absolute -right-0.5 -top-0.5 min-w-[8px] rounded-full bg-blue-500 p-0.5" />
                                )}
                              </Link>
                            </Tooltip>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {/* Expanded DMs — always on mobile, conditional on desktop */}
                  <div className={sidebarCollapsed ? 'lg:hidden' : ''}>
                  <div className="mb-1 flex items-center justify-between px-3">
                    <div className="flex items-center gap-1.5">
                      <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                        Direct messages
                      </h3>
                      {totalDmUnread > 0 && (
                        <span className="min-w-[18px] rounded-full bg-blue-500 px-1 py-0.5 text-center text-[9px] font-bold leading-none text-white">
                          {totalDmUnread > 99 ? '99+' : totalDmUnread}
                        </span>
                      )}
                    </div>
                    <Link
                      to="/org/$orgSlug/messages"
                      params={{ orgSlug: currentOrgSlug }}
                      className="rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
                      title="New message"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                      </svg>
                    </Link>
                  </div>
                  <div className="space-y-0.5">
                    {[...dms].sort((a, b) => {
                      if (!a.lastMessage && !b.lastMessage) return 0;
                      if (!a.lastMessage) return 1;
                      if (!b.lastMessage) return -1;
                      return new Date(b.lastMessage.createdAt).getTime() - new Date(a.lastMessage.createdAt).getTime();
                    }).map((dm) => {
                      const unread = countsByArea[dm.id] ?? 0;
                      const otherParticipants = dm.participants.filter((p) => p.userId !== user?.id);
                      const dmDisplayName = otherParticipants.map((p) => p.displayName).join(', ') || dm.name;
                      const dmTypers = (typingByArea[dm.id] ?? []).filter((t) => t.userId !== user?.id);
                      return (
                        <Link
                          key={dm.id}
                          to="/org/$orgSlug/$areaSlug"
                          params={{ orgSlug: currentOrgSlug, areaSlug: dm.id }}
                          className="flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-800 [&.active]:bg-blue-50 [&.active]:font-medium [&.active]:text-blue-700 dark:[&.active]:bg-blue-900/40 dark:[&.active]:text-blue-300"
                        >
                          <DmAvatars participants={otherParticipants.length > 0 ? otherParticipants : dm.participants} />
                          <div className="min-w-0 flex-1">
                            <span className={`block truncate ${unread > 0 ? 'font-semibold text-gray-900 dark:text-white' : ''}`}>{dmDisplayName}</span>
                            {dmTypers.length > 0 ? (
                              <span className="block truncate text-xs italic text-blue-500 dark:text-blue-400">
                                {dmTypers.map((t) => t.displayName).join(', ')} typing
                                <span className="ml-0.5 inline-flex gap-0.5">
                                  <span className="inline-block h-1 w-1 animate-bounce rounded-full bg-blue-500 [animation-delay:0ms]" />
                                  <span className="inline-block h-1 w-1 animate-bounce rounded-full bg-blue-500 [animation-delay:150ms]" />
                                  <span className="inline-block h-1 w-1 animate-bounce rounded-full bg-blue-500 [animation-delay:300ms]" />
                                </span>
                              </span>
                            ) : dm.lastMessage ? (
                              <span className="block truncate text-xs text-gray-400">
                                {dm.lastMessage.isDeleted
                                  ? 'Message deleted'
                                  : dm.lastMessage.content}
                              </span>
                            ) : null}
                          </div>
                          {unread > 0 && (
                            <span className="min-w-[20px] rounded-full bg-blue-500 px-1.5 py-0.5 text-center text-[10px] font-bold text-white">
                              {unread > 99 ? '99+' : unread}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                    {dms.length === 0 && (
                      <p className="px-3 py-1 text-xs text-gray-400">No messages yet</p>
                    )}
                  </div>
                  </div>
                </>
              )}


            </div>
          ) : (
            <p className={`px-3 py-2 text-sm text-gray-400 ${sidebarCollapsed ? 'lg:hidden' : ''}`}>No organization selected</p>
          )}
        </nav>

        {/* Collapse toggle — desktop only */}
        <div className={`hidden border-t border-gray-200 lg:flex dark:border-gray-800 ${sidebarCollapsed ? 'justify-center p-1' : 'justify-end px-3 py-1'}`}>
          <button
            onClick={toggleSidebarCollapsed}
            className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? (
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
            ) : (
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7M19 19l-7-7 7-7" />
              </svg>
            )}
          </button>
        </div>

        {/* User footer */}
        <div className={`border-t border-gray-200 p-3 dark:border-gray-800 ${sidebarCollapsed ? 'lg:p-1' : ''}`}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleProfilePictureUpload(file);
              e.target.value = '';
            }}
          />
          {/* Collapsed user footer — just profile picture */}
          {sidebarCollapsed && (
            <div className="hidden lg:flex lg:justify-center">
              <DropdownMenu.Root>
                <DropdownMenu.Trigger className="rounded-lg p-1 transition-colors hover:bg-gray-200 dark:hover:bg-gray-800">
                  <div className="relative flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-medium text-blue-700 overflow-hidden dark:bg-blue-900 dark:text-blue-300">
                    {user?.profilePictureUrl ? (
                      <img src={user.profilePictureUrl} alt={user.displayName ?? 'Profile'} className="h-full w-full object-cover" />
                    ) : (
                      user?.displayName?.charAt(0).toUpperCase() ?? '?'
                    )}
                  </div>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content
                    className="z-50 min-w-[180px] rounded-lg border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-gray-900"
                    sideOffset={4}
                    align="start"
                    side="right"
                  >
                    <div className="px-2 py-1.5">
                      <p className="text-sm font-medium">{user?.fullName}</p>
                      <p className="text-xs text-gray-600">{user?.homeCity ?? 'No city set'}</p>
                    </div>
                    <DropdownMenu.Separator className="my-1 h-px bg-gray-200 dark:bg-gray-700" />
                    <DropdownMenu.Item
                      className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                      onSelect={() => setEditProfileDialogOpen(true)}
                    >
                      Edit profile
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                      onSelect={() => fileInputRef.current?.click()}
                    >
                      Change picture
                    </DropdownMenu.Item>
                    <DropdownMenu.Separator className="my-1 h-px bg-gray-200 dark:bg-gray-700" />
                    {!isGuest && (
                      <>
                        <DropdownMenu.Item
                          className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                          onSelect={() => setStatusDialogOpen(true)}
                        >
                          {myStatus ? `${myStatus.emoji ?? ''} Change status` : 'Set status'}
                        </DropdownMenu.Item>
                        <DropdownMenu.Item
                          className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                          onSelect={() => setDndDialogOpen(true)}
                        >
                          {isDndActive ? 'DND settings (active)' : 'Do Not Disturb'}
                        </DropdownMenu.Item>
                        <DropdownMenu.Separator className="my-1 h-px bg-gray-200 dark:bg-gray-700" />
                      </>
                    )}
                    <ThemeMenuItem />
                    <DropdownMenu.Separator className="my-1 h-px bg-gray-200 dark:bg-gray-700" />
                    <DropdownMenu.Item
                      className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-red-600 outline-none transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950"
                      onSelect={() => {
                        // logout() does not reset sibling stores, so each
                        // sign-out site clears its own — otherwise the previous
                        // user's total lingers until the next seed resolves.
                        useTodosStore.getState().reset();
                        logout();
                        navigateWithTransition(() => navigate({ to: '/login' }));
                      }}
                    >
                      Sign out
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            </div>
          )}
          {/* Expanded user footer — full content */}
          <div className={sidebarCollapsed ? 'lg:hidden' : ''}>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 transition-colors hover:bg-gray-200 dark:hover:bg-gray-800">
              {/* span, not button — the Radix trigger IS a button and nested
                  buttons are invalid HTML (console errors on every render) */}
              <span
                className="relative flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-blue-100 text-sm font-medium text-blue-700 outline-none ring-offset-2 hover:ring-2 hover:ring-blue-400 dark:bg-blue-900 dark:text-blue-300 overflow-hidden"
                title="Change profile picture"
                onPointerDown={(e) => {
                  // Keep the menu closed when clicking the avatar itself
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  fileInputRef.current?.click();
                }}
              >
                {uploadingPicture ? (
                  <svg className="h-4 w-4 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                ) : user?.profilePictureUrl ? (
                  <img
                    src={user.profilePictureUrl}
                    alt={user.displayName ?? 'Profile'}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  user?.displayName?.charAt(0).toUpperCase() ?? '?'
                )}
              </span>
              <div className="min-w-0 flex-1 text-left">
                <p className="truncate text-sm font-medium">{user?.displayName}</p>
                {myStatus && (
                  <p className="truncate text-xs text-gray-600 dark:text-gray-300">
                    {myStatus.emoji ? `${myStatus.emoji} ` : ''}{myStatus.text}
                  </p>
                )}
                {isDndActive && (
                  <p className="text-xs text-amber-600 dark:text-amber-400">DND active</p>
                )}
              </div>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className="z-50 min-w-[180px] rounded-lg border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-gray-900"
                sideOffset={4}
                align="start"
                side="top"
              >
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.fullName}</p>
                  <p className="text-xs text-gray-600">{user?.homeCity ?? 'No city set'}</p>
                </div>
                <DropdownMenu.Separator className="my-1 h-px bg-gray-200 dark:bg-gray-700" />
                <DropdownMenu.Item
                  className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                  onSelect={() => setEditProfileDialogOpen(true)}
                >
                  Edit profile
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                  onSelect={() => fileInputRef.current?.click()}
                >
                  Change picture
                </DropdownMenu.Item>
                {user?.profilePictureUrl && (
                  <DropdownMenu.Item
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-red-600 outline-none transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950"
                    onSelect={async () => {
                      try {
                        await removeProfilePicture();
                        const currentUser = useAuthStore.getState().user;
                        if (currentUser) {
                          const updatedUser = { ...currentUser, profilePictureUrl: null };
                          const at = useAuthStore.getState().accessToken!;
                          const rt = useAuthStore.getState().refreshToken!;
                          useAuthStore.getState().setAuth(updatedUser, at, rt);
                        }
                      } catch {
                        toast.error('Failed to remove profile picture');
                      }
                    }}
                  >
                    Remove picture
                  </DropdownMenu.Item>
                )}
                <DropdownMenu.Separator className="my-1 h-px bg-gray-200 dark:bg-gray-700" />
                {isGuest && (
                  <>
                    <DropdownMenu.Item
                      className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium text-blue-600 outline-none transition-colors hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950"
                      onSelect={() => setUpgradeDialogOpen(true)}
                    >
                      Upgrade to full account
                    </DropdownMenu.Item>
                    <DropdownMenu.Separator className="my-1 h-px bg-gray-200 dark:bg-gray-700" />
                  </>
                )}
                {!isGuest && (
                  <>
                    <DropdownMenu.Item
                      className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                      onSelect={() => setStatusDialogOpen(true)}
                    >
                      {myStatus ? `${myStatus.emoji ?? ''} Change status` : 'Set status'}
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                      onSelect={() => setDndDialogOpen(true)}
                    >
                      {isDndActive ? 'DND settings (active)' : 'Do Not Disturb'}
                    </DropdownMenu.Item>
                    <DropdownMenu.Separator className="my-1 h-px bg-gray-200 dark:bg-gray-700" />
                  </>
                )}
                <ThemeMenuItem />
                <DropdownMenu.Separator className="my-1 h-px bg-gray-200 dark:bg-gray-700" />
                {currentOrgSlug && (
                  <DropdownMenu.Item
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                    onSelect={() => navigateWithTransition(() => navigate({ to: '/org/$orgSlug/help', params: { orgSlug: currentOrgSlug } }))}
                  >
                    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Help
                  </DropdownMenu.Item>
                )}
                <DropdownMenu.Item
                  className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm outline-none transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                  onSelect={() => useOnboardingStore.getState().startTour()}
                >
                  <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  Take a tour
                </DropdownMenu.Item>
                <DropdownMenu.Separator className="my-1 h-px bg-gray-200 dark:bg-gray-700" />
                <DropdownMenu.Item
                  className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-red-600 outline-none transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950"
                  onSelect={() => {
                    useTodosStore.getState().reset();
                    logout();
                    navigateWithTransition(() => navigate({ to: '/login' }));
                  }}
                >
                  Sign out
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
          </div>
        </div>
      </aside>

      {/* Main content — memoized to prevent sidebar re-renders from cascading */}
      <main id="main-content" className="flex flex-1 flex-col overflow-hidden">
        {/* Mobile header bar with hamburger menu */}
        <div className="flex h-12 flex-shrink-0 items-center gap-2 border-b border-gray-200 px-3 lg:hidden dark:border-gray-800">
          <button
            onClick={() => setSidebarOpen(true)}
            className="rounded-lg p-1.5 text-gray-600 hover:bg-gray-100 active:bg-gray-200 dark:text-gray-300 dark:hover:bg-gray-800"
            aria-label="Open menu"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          {currentOrg && (
            <>
              <OrgIcon org={currentOrg} />
              <span className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{currentOrg.name}</span>
            </>
          )}
          {(() => {
            const total = inboxCounts.unread + inboxCounts.acknowledgmentsPending + inboxCounts.replyLater;
            return total > 0 ? (
              <Link
                to="/org/$orgSlug/inbox"
                params={{ orgSlug: currentOrgSlug ?? '' }}
                /* The only way to the Inbox from the mobile header, and it was
                   a 19px pill. py-1 lifts it over WCAG 2.2 SC 2.5.8's 24px. */
                className="ml-auto min-w-[20px] rounded-full bg-blue-500 px-1.5 py-1.5 text-center text-[10px] font-bold text-white"
              >
                {total > 99 ? '99+' : total}
              </Link>
            ) : null;
          })()}
        </div>
        {/* Global offline banner — visible on all pages when browser loses network */}
        {!isOnline && (
          <div role="alert" className="flex items-center justify-center gap-2 bg-red-50 px-4 py-2 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-200">
            <svg className="h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 5.636a9 9 0 010 12.728M5.636 18.364a9 9 0 010-12.728" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.536 8.464a5 5 0 010 7.072M8.464 15.536a5 5 0 010-7.072" />
              <line x1="4" y1="4" x2="20" y2="20" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
            </svg>
            You're offline. Some features may not work.
          </div>
        )}
        <MemoizedContent orgSwitching={orgSwitching} />
      </main>

      {/* Dialogs */}
      <CommandPalette open={commandPaletteOpen} onOpenChange={setCommandPaletteOpen} orgSlug={currentOrgSlug} />
      <SetStatusDialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen} />
      <DndSettings open={dndDialogOpen} onOpenChange={setDndDialogOpen} />
      <EditProfileDialog open={editProfileDialogOpen} onOpenChange={setEditProfileDialogOpen} />
      {isGuest && <UpgradeAccountDialog open={upgradeDialogOpen} onOpenChange={setUpgradeDialogOpen} />}
      {isAdmin && currentOrg && (
        <CreateAreaDialog open={createAreaOpen} onOpenChange={setCreateAreaOpen} orgId={currentOrg.id} />
      )}

      {/* Complete Profile Dialog — blocks app until displayName + profile picture are set */}
      <Dialog.Root open={profileIncomplete} onOpenChange={() => {}}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
          <Dialog.Content
            className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-1.5rem)] max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-xl border border-gray-200 bg-white p-4 shadow-xl sm:w-full sm:p-6 dark:border-gray-700 dark:bg-gray-900"
            onPointerDownOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <Dialog.Title className="text-lg font-semibold">
              Complete Your Profile
            </Dialog.Title>
            <Dialog.Description className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {profileMissingName && profileMissingPicture
                ? 'Please set a display name and upload a profile picture to continue.'
                : profileMissingPicture
                  ? 'Please upload a profile picture to continue.'
                  : 'Please set a display name to continue.'}
            </Dialog.Description>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const trimmed = profileDisplayName.trim();
                if (!trimmed) {
                  setProfileError('Display name is required');
                  return;
                }
                if (profileMissingPicture && !profilePictureFile && !profilePicturePreview) {
                  setProfileError('Profile picture is required');
                  return;
                }
                setProfileError('');
                setProfileSaving(true);
                try {
                  // Upload profile picture if a new file was selected
                  let newProfilePictureUrl: string | null = null;
                  if (profilePictureFile) {
                    setProfilePictureUploading(true);
                    const { uploadUrl, fileKey } = await presignProfilePicture(
                      profilePictureFile.name,
                      profilePictureFile.type,
                      profilePictureFile.size,
                    );
                    const uploadRes = await fetch(uploadUrl, {
                      method: 'PUT',
                      body: profilePictureFile,
                      headers: { 'Content-Type': profilePictureFile.type },
                    });
                    if (!uploadRes.ok) {
                      throw new Error('Failed to upload image');
                    }
                    const { profilePictureUrl: url } = await completeProfilePicture(fileKey);
                    newProfilePictureUrl = url;
                    setProfilePictureUploading(false);
                  }

                  // Update display name
                  const res = await updateProfile({ displayName: trimmed });

                  // Update user in auth store
                  const store = useAuthStore.getState();
                  if (store.user) {
                    store.setAuth(
                      {
                        ...store.user,
                        displayName: res.user.displayName,
                        ...(newProfilePictureUrl ? { profilePictureUrl: newProfilePictureUrl } : {}),
                      },
                      store.accessToken!,
                      store.refreshToken!,
                    );
                  }
                  // Revoke blob URL on dialog close to avoid memory leak
                  if (profilePicturePreview && profilePicturePreview.startsWith('blob:')) {
                    URL.revokeObjectURL(profilePicturePreview);
                  }
                  setProfileIncomplete(false);
                } catch {
                  setProfilePictureUploading(false);
                  setProfileError('Failed to save. Please try again.');
                } finally {
                  setProfileSaving(false);
                }
              }}
              className="mt-4 space-y-4"
            >
              {profileError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
                  {profileError}
                </div>
              ) : null}

              {/* Profile Picture Upload */}
              {(profileMissingPicture || profilePictureFile) && (
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Profile Picture
                  </label>
                  <input
                    ref={profilePicInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        if (!file.type.startsWith('image/')) {
                          setProfileError('Please select an image file');
                          return;
                        }
                        if (file.size > 5 * 1024 * 1024) {
                          setProfileError('Image must be under 5MB');
                          return;
                        }
                        setProfileError('');
                        setProfilePictureFile(file);
                        // Revoke previous blob URL to avoid memory leak
                        if (profilePicturePreview && profilePicturePreview.startsWith('blob:')) {
                          URL.revokeObjectURL(profilePicturePreview);
                        }
                        setProfilePicturePreview(URL.createObjectURL(file));
                      }
                      e.target.value = '';
                    }}
                  />
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      onClick={() => profilePicInputRef.current?.click()}
                      className="group relative flex h-20 w-20 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-gray-300 bg-gray-50 transition-colors hover:border-blue-400 hover:bg-blue-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:border-blue-500 dark:hover:bg-blue-950"
                    >
                      {profilePicturePreview ? (
                        <>
                          <img
                            src={profilePicturePreview}
                            alt="Profile preview"
                            className="h-full w-full object-cover"
                          />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                            <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </div>
                        </>
                      ) : (
                        <svg className="h-8 w-8 text-gray-400 group-hover:text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      )}
                    </button>
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      <p className="font-medium">
                        {profilePicturePreview ? 'Click to change' : 'Click to upload'}
                      </p>
                      <p className="mt-0.5 text-xs text-gray-400">
                        JPG, PNG, WebP or GIF. Max 5MB.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Display Name */}
              <div>
                <label
                  htmlFor="profile-display-name"
                  className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Display Name
                </label>
                <input
                  id="profile-display-name"
                  type="text"
                  required
                  autoFocus={profileMissingName}
                  value={profileDisplayName}
                  onChange={(e) => setProfileDisplayName(e.target.value)}
                  placeholder="How others will see you"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm outline-none transition-colors placeholder:text-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500"
                />
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={profileSaving || profilePictureUploading}
                  className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  {profilePictureUploading ? 'Uploading...' : profileSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      <OnboardingTour />
      <ToastContainer />
    </div>
  );
}

function OrgIcon({ org }: { org: { logoUrl?: string | null; color: string | null; name: string } }) {
  if (org.logoUrl) {
    return (
      <img
        src={org.logoUrl}
        alt={org.name}
        className="h-7 w-7 rounded-md object-cover"
      />
    );
  }
  return (
    <div
      className="flex h-7 w-7 items-center justify-center rounded-md text-sm font-semibold"
      style={{ backgroundColor: org.color ?? '#6366f1', color: '#fff' }}
    >
      {org.name.charAt(0).toUpperCase()}
    </div>
  );
}

function ChevronDown() {
  return (
    <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function DmAvatars({
  participants,
}: {
  participants: { userId: string; displayName: string; profilePictureUrl: string | null }[];
}) {
  const shown = participants.slice(0, 2);
  return (
    <div className="flex -space-x-1">
      {shown.map((p) => (
        <div
          key={p.userId}
          className="flex h-6 w-6 items-center justify-center rounded-full border border-white bg-gray-200 text-[10px] font-medium text-gray-600 dark:border-gray-900 dark:bg-gray-700 dark:text-gray-300"
        >
          {p.displayName.charAt(0).toUpperCase()}
        </div>
      ))}
    </div>
  );
}

function ThemeMenuItem() {
  const { mode, setMode } = useThemeStore();
  const modes = [
    { value: 'light' as const, label: 'Light', icon: (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    )},
    { value: 'dark' as const, label: 'Dark', icon: (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
      </svg>
    )},
    { value: 'system' as const, label: 'System', icon: (
      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    )},
  ];

  return (
    <div className="flex items-center gap-1 px-2 py-1.5">
      <span className="mr-auto text-sm text-gray-600 dark:text-gray-300">Theme</span>
      {modes.map((m) => (
        <button
          key={m.value}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setMode(m.value);
          }}
          className={`rounded-md p-1.5 transition-colors ${
            mode === m.value
              ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
              : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300'
          }`}
          title={m.label}
        >
          {m.icon}
        </button>
      ))}
    </div>
  );
}
